import type { MatchResolverContext, ResolverPlayer } from 'modules/manager/domain/placeholder';
import {
  ReviewClip,
  ReviewClipKind,
  ReviewFrameProvider,
  ReviewGoal,
  VideoReviewOptions,
} from 'modules/match-video/domain/interfaces';
import { buildPlateContext } from './plate-context';
import { renderTemplateFrame, TemplateFrameSource } from './render-template-frame';

/** Everything the intro and the plates are drawn from. */
export interface ReviewDesign {
  match: MatchResolverContext;
  /** Players of the match protocol by id — scorers and assistants of the plates. */
  players: ReadonlyMap<number, ResolverPlayer>;
  /** `null` — a black intro. */
  intro: TemplateFrameSource | null;
  /** Plate template per clip kind; a missing one — clips of that kind go without a plate. */
  plates: Partial<Record<ReviewClipKind, TemplateFrameSource>>;
  goalByClipId: Readonly<Record<string, ReviewGoal>>;
  /** Protocol goals in match-time order (`ReviewGoalMatch.goals`) — the score right after a plate's goal. */
  goals: readonly ReviewGoal[];
  options: VideoReviewOptions;
}

/** Renders the intro and every plate from the design's templates, each on demand (`ReviewFrameProvider`). */
export function createReviewFrameProvider(design: ReviewDesign): ReviewFrameProvider {
  return {
    intro: async () => (design.intro ? renderTemplateFrame(design.intro, design.match) : null),
    plate: async (clip: ReviewClip) => {
      if (design.options.plates === 'none') return null;
      const template = design.plates[clip.kind];
      if (!template) return null;
      const goal = clip.kind === 'goal' ? design.goalByClipId[clip.id] : undefined;
      return renderTemplateFrame(template, buildPlateContext(design.match, goal, design.goals, design.players));
    },
  };
}
